import * as React from 'react';
import styles from './index.less';
import { Checkbox, Space, Tabs } from 'antd';
import FDrawer from '@/components/FDrawer';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import FResourceTypeFilter3 from '@/components/FResourceTypeFilter3';
import FComponentsLib from '@freelog/components-lib';
import FIcons from '@/components/FIcons';
import FCoverImage from '@/components/FCoverImage';
import * as AHooks from 'ahooks';
import dayjs from 'dayjs';
import fMessage from '@/components/fMessage';
import AddItemsFromResourcesDrawer from '../AddItemsFromResourcesDrawer';
import FListFooter, { listStateAndListMore } from '@/components/FListFooter';
import FLoadingTip from '@/components/FLoadingTip';
import FNoDataTip from '@/components/FNoDataTip';
import FResourceStatusBadge from '@/components/FResourceStatusBadge';
import FInput_Search from '@/components/FInput_Search';
import FTooltip from '@/components/FTooltip';
import FSimpleBar from '@/components/FSimpleBar';
import FHotspotTooltip, { fSetHotspotTooltipVisible } from '@/components/FHotspotTooltip';
import img_choiceLabel from '@/assets/choice-label@2x.png';

interface AddItemsFromExhibitsDrawerProps {
  open: boolean;
  nodeID: number;
  exhibitID: string;
  onClose?: () => void;
  onChange?: () => void;
}

interface ExhibitRow {
  added: boolean;
  id: string;
  cover: string;
  title: string;
  name: string;
  resourceType: string[];
  updateDate: string;
  status: 'unreleased' | 'online' | 'freeze' | 'offline';
  isOnline: boolean;
  resourceInfo: {
    id: string;
    name: string;
    title: string;
    status: 0 | 1 | 2 | 4;
  };
  isFreeze: boolean;
  isAuth: boolean;
  authErrorText: string;
  isChoice: boolean;
}

interface AddItemsFromExhibitsDrawerStates {
  selectedResourceType: {
    value: string;
    label: string;
    values: string[];
    labels: string[];
  };
  keywordsInputFilter: string;
  listState: 'loading' | 'noData' | 'noSearchResult' | 'loaded';
  listMore: 'loading' | 'andMore' | 'noMore';
  exhibitList: ExhibitRow[];
  checkedPresentableIds: string[];
}

const defaultStates: AddItemsFromExhibitsDrawerStates = {
  selectedResourceType: {
    value: '#all',
    label:
      // '所有类型'
      FI18n.i18nNext.t('filter_resourcetype_option_all'),
    values: ['#all'],
    labels: [
      // '所有类型'
      FI18n.i18nNext.t('filter_resourcetype_option_all'),
    ],
  },
  keywordsInputFilter: '',
  listState: 'loading',
  listMore: 'loading',
  exhibitList: [],
  checkedPresentableIds: [],
};

const HOTSPOT_CONFIRM_ID = 'node.createdCollectionExhibit.addExhibitsDrawer.confirmButton' as const;

function mapExhibitStatus(
  isFreeze: boolean,
  isOnline: boolean,
  resourceStatus: 0 | 1 | 2 | 4,
): ExhibitRow['status'] {
  if (isFreeze) {
    return 'freeze';
  }
  if (!isOnline) {
    return 'offline';
  }
  if (resourceStatus === 0) {
    return 'unreleased';
  }
  if (resourceStatus === 4) {
    return 'offline';
  }
  return 'online';
}

function AddItemsFromExhibitsDrawer({
  open,
  nodeID,
  exhibitID,
  onClose,
  onChange,
}: AddItemsFromExhibitsDrawerProps) {
  const [$selectedResourceType, set$selectedResourceType, get$selectedResourceType] =
    FUtil.Hook.useGetState<AddItemsFromExhibitsDrawerStates['selectedResourceType']>(
      defaultStates.selectedResourceType,
    );
  const [$keywordsInputFilter, set$keywordsInputFilter, get$keywordsInputFilter] =
    FUtil.Hook.useGetState<AddItemsFromExhibitsDrawerStates['keywordsInputFilter']>(
      defaultStates.keywordsInputFilter,
    );
  const [$listState, set$listState] = FUtil.Hook.useGetState<
    AddItemsFromExhibitsDrawerStates['listState']
  >(defaultStates.listState);
  const [$listMore, set$listMore, get$listMore] = FUtil.Hook.useGetState<
    AddItemsFromExhibitsDrawerStates['listMore']
  >(defaultStates.listMore);
  const [$exhibitList, set$exhibitList, get$exhibitList] = FUtil.Hook.useGetState<
    AddItemsFromExhibitsDrawerStates['exhibitList']
  >(defaultStates.exhibitList);
  const [$checkedPresentableIds, set$checkedPresentableIds, get$checkedPresentableIds] =
    FUtil.Hook.useGetState<AddItemsFromExhibitsDrawerStates['checkedPresentableIds']>(
      defaultStates.checkedPresentableIds,
    );

  const [$openResourceDrawer, set$openResourceDrawer] = FUtil.Hook.useGetState<boolean>(false);

  AHooks.useDebounceEffect(
    () => {
      if (!open) {
        return;
      }
      void fetchExhibitList(true);
    },
    [open, $selectedResourceType, $keywordsInputFilter],
    {
      wait: 300,
    },
  );

  AHooks.useDebounceEffect(
    () => {
      if (!open) {
        return;
      }
      if (get$checkedPresentableIds().length > 0) {
        fSetHotspotTooltipVisible(HOTSPOT_CONFIRM_ID, {
          value: true,
          effectiveImmediately: true,
          onlyNullish: true,
        });
      }
    },
    [open, $checkedPresentableIds],
    {
      wait: 10,
    },
  );

  function changeChecked(presentableId: string) {
    if (get$checkedPresentableIds().includes(presentableId)) {
      set$checkedPresentableIds(
        get$checkedPresentableIds().filter((v) => {
          return v !== presentableId;
        }),
      );
    } else {
      set$checkedPresentableIds([...get$checkedPresentableIds(), presentableId]);
    }
  }

  async function fetchExhibitList(restart: boolean = true) {
    const {
      data: data_NodeInfo,
    }: {
      data: {
        nodeId: number;
        nodeTitle: string;
        nodeName: string;
      };
    } = await FServiceAPI.Node.details({
      nodeId: nodeID,
    });

    const oldList: ExhibitRow[] = restart ? [] : get$exhibitList();
    if (restart) {
      set$listState('loading');
    } else {
      set$listMore('loading');
    }

    const resourceTypes: string[] = get$selectedResourceType().value.split('#');
    let resourceTypeCode: string = resourceTypes[0];
    let resourceType: string = '';

    if (resourceTypes.length > 1 && resourceTypes[resourceTypes.length - 1] === 'other') {
      resourceTypeCode = resourceTypes[0];
    }

    if (resourceTypes.length > 1 && resourceTypes[resourceTypes.length - 1] === 'all') {
      resourceType =
        get$selectedResourceType().labels[get$selectedResourceType().labels.length - 2];
    }

    const params: Parameters<typeof FServiceAPI.Exhibit.presentables>[0] = {
      nodeId: nodeID,
      limit: FUtil.Predefined.pageSize,
      skip: oldList.length,
      keywords: get$keywordsInputFilter() || undefined,
      resourceType: resourceType || undefined,
      resourceTypeCode: resourceType === '' ? resourceTypeCode || undefined : undefined,
      omitResourceType: '主题',
      isLoadResourceDetailInfo: 1,
      subjectType: 1,
      onlineStatus: 2,
    };

    const {
      data: data_Exhibits,
    }: {
      data: {
        dataList: {
          presentableId: string;
          presentableIntro: string;
          presentableName: string;
          presentableTitle: string;
          coverImages: string[];
          version: string;
          onlineStatus: 0 | 1;
          updateDate?: string;
          createDate?: string;
          operationType?: number;
          resourceInfo: {
            resourceId: string;
            resourceName: string;
            resourceTitle: string;
            resourceType: string[];
            serializeStatus: 0 | 1;
            resourceVersions: {
              versionId: string;
            }[];
            latestVersion: string;
            status: 0 | 1 | 2 | 4;
          };
          policies: any[];
          subjectType: 1 | 4 | 5;
        }[];
        totalItem: number;
      };
    } = await FServiceAPI.Exhibit.presentables(params);

    let data_IsExist: {
      isExists: boolean;
      mountId: string;
    }[] = [];
    if (data_Exhibits.dataList.length > 0) {
      const {
        data,
      }: {
        data: {
          isExists: boolean;
          mountId: string;
        }[];
      } = await FServiceAPI.Exhibit.getItemsFromPresentableCollectionIsExist({
        presentableId: exhibitID,
        mountIds: data_Exhibits.dataList.map((i) => i.presentableId).join(','),
        idType: 'presentableId',
      });
      data_IsExist = data;
    }

    let batchAuthPs: {
      error: string;
      isAuth: boolean;
      presentableId: string;
    }[] = [];
    if (data_Exhibits.dataList.length > 0) {
      const params1: Parameters<typeof FServiceAPI.Exhibit.batchAuth>[0] = {
        nodeId: nodeID,
        authType: 3,
        presentableIds: data_Exhibits.dataList.map<string>((dl) => {
          return dl.presentableId;
        }).join(','),
      };
      const {
        data: data1,
      }: {
        data: {
          error: string;
          isAuth: boolean;
          presentableId: string;
        }[];
      } = await FServiceAPI.Exhibit.batchAuth(params1);
      batchAuthPs = data1;
    }

    const exhibitList = [
      ...oldList,
      ...data_Exhibits.dataList.map<ExhibitRow>((i) => {
        const isAdded = data_IsExist.find((j) => j.mountId === i.presentableId)?.isExists ?? false;

        const authInfo = batchAuthPs.find((bap) => {
          return bap.presentableId === i.presentableId;
        });
        const isFreeze = i.resourceInfo.status === 2;
        const isOnline = i.onlineStatus === 1;
        const dateRaw = i.updateDate || i.createDate;
        return {
          added: isAdded,
          id: i.presentableId,
          cover: i.coverImages[0],
          title: i.presentableTitle,
          name: data_NodeInfo.nodeName + '/' + i.presentableName,
          resourceType: i.resourceInfo.resourceType,
          updateDate: dateRaw
            ? dayjs(dateRaw).format(FUtil.Predefined.momentDateFormat)
            : '',
          status: mapExhibitStatus(isFreeze, isOnline, i.resourceInfo.status),
          isOnline,
          resourceInfo: {
            id: i.resourceInfo.resourceId,
            name: i.resourceInfo.resourceName,
            title: i.resourceInfo.resourceTitle,
            status: i.resourceInfo.status,
          },
          isFreeze,
          isAuth: authInfo?.isAuth ?? false,
          authErrorText: authInfo?.error ?? '',
          isChoice: i.operationType === 5,
        };
      }),
    ];

    const { state, more } = listStateAndListMore({
      list_Length: exhibitList.length,
      total_Length: data_Exhibits.totalItem,
      has_FilterCriteria:
        get$keywordsInputFilter() !== '' || get$selectedResourceType().value !== '#all',
    });

    set$listState(state);
    set$listMore(more);
    set$exhibitList(exhibitList);
  }

  /** 未勾选时才拦截：冻结不可新选、满 100 不可再选。已选中永远不挡遮罩，保证多选框可取消勾选 */
  function isRowSelectBlocked(d: ExhibitRow, rowChecked: boolean): boolean {
    if (rowChecked) {
      return false;
    }
    return (
      d.status === 'freeze' ||
      (!d.added &&
        $checkedPresentableIds.length >= 100 &&
        !$checkedPresentableIds.includes(d.id))
    );
  }

  async function removeAddedPresentableFromCollection(presentableId: string) {
    const { ret, errCode, msg } = await FServiceAPI.Exhibit.removeItemsFromPresentableCollection({
      presentableId: exhibitID,
      removeIds: presentableId,
      idType: 'presentableId',
    });
    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }
    set$exhibitList(
      get$exhibitList().map((j) => {
        return j.id === presentableId ? { ...j, added: false } : j;
      }),
    );
    onChange?.();
  }

  return (
    <FDrawer
      open={open}
      title={
        // 添加单品抽屉标题
        FI18n.i18nNext.tAuto('cqr_itemmgmt_additem_title')
      }
      width={720}
      onClose={() => {
        onClose?.();
      }}
      topRight={
        <Space size={30}>
          <FComponentsLib.FTextBtn
            onClick={() => {
              onClose?.();
            }}
            type='default'
          >
            {
              // 取消
              FI18n.i18nNext.tAuto('cqr_itemmgmt_additem_btn_cancel')
            }
          </FComponentsLib.FTextBtn>
          <FHotspotTooltip
            id={HOTSPOT_CONFIRM_ID}
            style={{ left: 20, bottom: -40 }}
            text={
              // 引导点击添加按钮的提示
              FI18n.i18nNext.tAuto('hotpots_cqr_additem_add')
            }
            onOpenChange={(o) => {
              if (o) {
                fSetHotspotTooltipVisible(HOTSPOT_CONFIRM_ID, {
                  value: false,
                  effectiveImmediately: false,
                  onlyNullish: false,
                });
              }
            }}
          >
            <FComponentsLib.FRectBtn
              disabled={$checkedPresentableIds.length === 0}
              onClick={async () => {
                fSetHotspotTooltipVisible(HOTSPOT_CONFIRM_ID, {
                  value: false,
                  effectiveImmediately: true,
                  onlyNullish: false,
                });
                const ids = get$checkedPresentableIds();
                if (ids.length === 0) {
                  return;
                }
                const {
                  ret,
                  errCode,
                  msg,
                  data,
                }: {
                  ret: number;
                  errCode: number;
                  msg: string;
                  data: {
                    addFailedItems: {
                      presentableId: string;
                      reason: string;
                    }[];
                  };
                } = await FServiceAPI.Exhibit.addItemsToPresentableCollection({
                  presentableId: exhibitID,
                  addCollectionItems: ids.map((id) => ({ presentableId: id })),
                });
                if (ret !== 0 || errCode !== 0) {
                  fMessage(msg, 'error');
                  return;
                }
                const failed = new Set(data.addFailedItems.map((f) => f.presentableId));
                if (data.addFailedItems.length > 0) {
                  fMessage(data.addFailedItems.map((j) => j.reason).join('\n'), 'error');
                }
                set$exhibitList(
                  get$exhibitList().map((j) => {
                    if (ids.includes(j.id) && !failed.has(j.id)) {
                      return { ...j, added: true };
                    }
                    return j;
                  }),
                );
                set$checkedPresentableIds(
                  get$checkedPresentableIds().filter((id) => {
                    if (!ids.includes(id)) {
                      return true;
                    }
                    return failed.has(id);
                  }),
                );
                if (failed.size > 0 && failed.size < ids.length) {
                  onChange?.();
                }
                if (failed.size === 0) {
                  onClose?.();
                }
              }}
            >
              {
                // 添加到合集
                FI18n.i18nNext.tAuto('cqr_itemmgmt_additem_btn_add')
              }
            </FComponentsLib.FRectBtn>
          </FHotspotTooltip>
        </Space>
      }
      style={{ overflowY: 'auto', overflowX: 'hidden' }}
      contentStyle={{ padding: 0 }}
      drawerBodyStyle={{ width: '100%' }}
      afterOpenChange={(o) => {
        if (!o) {
          set$listState(defaultStates.listState);
          set$listMore(defaultStates.listMore);
          set$exhibitList(defaultStates.exhibitList);
          set$keywordsInputFilter(defaultStates.keywordsInputFilter);
          set$selectedResourceType(defaultStates.selectedResourceType);
          set$checkedPresentableIds(defaultStates.checkedPresentableIds);
        }
      }}
    >
      <div className={styles.container}>
        <div style={{ height: 8 }} />
        <div style={{ padding: '0 30px' }}>
          <Tabs
            items={[
              {
                key: '1',
                label:
                  // 节点展品 Tab 文案
                  FI18n.i18nNext.tAuto('additem_exhibit_title'),
              },
            ]}
          />
        </div>

        {$listState === 'noData' && (
          <FNoDataTip
            height={600}
            tipText={
              // 添加单品抽屉空列表提示
              FI18n.i18nNext.tAuto('cqr_itemmgmt_additem_empty_msg')
            }
          />
        )}

        {$listState !== 'noData' && (
          <>
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 30px',
              }}
            >
              <Space size={30}>
                <FResourceTypeFilter3
                  subjectTypeArr={['resource']}
                  value={$selectedResourceType}
                  omitTheme={true}
                  onChange={(value) => {
                    set$selectedResourceType(value);
                  }}
                />
                <FComponentsLib.FTextBtn
                  type={'primary'}
                  onClick={() => {
                    set$openResourceDrawer(true);
                  }}
                >
                  {
                    // 从资源市场添加展品
                    FI18n.i18nNext.tAuto('additem_exhibit_addmoreexhibit')
                  }
                </FComponentsLib.FTextBtn>
                <FComponentsLib.FContentText
                  type={'normal'}
                  text={
                    // 已选数量
                    FI18n.i18nNext.t('cqr_itemmgmt_additem_selectedqty', {
                      selectedqty: $checkedPresentableIds.length,
                    })
                  }
                  style={{ color: '#999' }}
                />
              </Space>

              <FInput_Search
                value={$keywordsInputFilter}
                onChange={(e) => {
                  set$keywordsInputFilter(e.target.value);
                }}
                placeholder={
                  // 展品列表搜索框占位
                  FI18n.i18nNext.t('additem_exhibit_exhlist_search_hint')
                }
              />
            </div>
            <div style={{ height: 20 }} />

            {$listState === 'loading' && <FLoadingTip height={600} />}

            {$listState === 'noSearchResult' && <FNoDataTip height={600} tipText={'无搜索数据'} />}

            {$listState === 'loaded' && (
              <FSimpleBar height={'calc(100vh - 200px)'}>
                <div style={{ padding: '0 30px' }}>
                  <div className={styles.list}>
                    {$exhibitList.map((d) => {
                      const rowChecked =
                        $checkedPresentableIds.includes(d.id) || d.added;
                      const blocked = isRowSelectBlocked(d, rowChecked);
                      const checkboxDisabled = d.status === 'freeze' && !rowChecked;
                      return (
                        <div
                          className={[
                            styles.item,
                            blocked ? styles.disabled : '',
                            rowChecked ? styles.selected : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          key={d.id}
                        >
                          <div className={styles.resource}>
                            <div
                              className={blocked ? styles.partFaded : undefined}
                              style={{
                                width: 16,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Checkbox
                                disabled={checkboxDisabled}
                                checked={rowChecked}
                                onChange={(e) => {
                                  if (checkboxDisabled) {
                                    return;
                                  }
                                  if (
                                    !d.added &&
                                    $checkedPresentableIds.length >= 100 &&
                                    !$checkedPresentableIds.includes(d.id)
                                  ) {
                                    return;
                                  }
                                  if (d.added) {
                                    if (!e.target.checked) {
                                      void removeAddedPresentableFromCollection(d.id);
                                    }
                                    return;
                                  }
                                  changeChecked(d.id);
                                }}
                              />
                            </div>
                            <div style={{ width: 20 }} />
                            <div
                              className={styles.resourceInfo}
                              onClick={() => {
                                if (d.added) {
                                  void removeAddedPresentableFromCollection(d.id);
                                  return;
                                }
                                if (blocked) {
                                  return;
                                }
                                changeChecked(d.id);
                              }}
                            >
                              <div className={styles.cover}>
                                <div
                                  className={[styles.coverImage, blocked ? styles.partFaded : '']
                                    .filter(Boolean)
                                    .join(' ')}
                                >
                                  <FCoverImage
                                    src={d.cover}
                                    width={104}
                                    style={{ borderRadius: 4 }}
                                  />
                                </div>
                                <div className={styles.coverOverlay}>
                                  <FResourceStatusBadge
                                    status={d.status}
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 600,
                                      lineHeight: 1.6,
                                      padding: '0 3px',
                                      borderRadius: 3,
                                      maxWidth: '100%',
                                      minWidth: 0,
                                    }}
                                  />
                                  {d.isChoice && (
                                    <div className={blocked ? styles.partFaded : undefined}>
                                      <FTooltip title={'编辑精选'} placement={'bottomLeft'}>
                                        <img
                                          src={img_choiceLabel}
                                          alt={''}
                                          style={{ width: 20, height: 20 }}
                                        />
                                      </FTooltip>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div
                                className={[styles.content, blocked ? styles.partFaded : '']
                                  .filter(Boolean)
                                  .join(' ')}
                              >
                                <FComponentsLib.FContentText
                                  text={d.title}
                                  type={'highlight'}
                                  singleRow
                                />
                                <Space size={5}>
                                  <FComponentsLib.FContentText
                                    text={d.resourceType.join('/')}
                                    type={'additional2'}
                                  />
                                  <FComponentsLib.FContentText text={'|'} type={'additional2'} />
                                  <FComponentsLib.FContentText
                                    text={
                                      // 最近更新时间
                                      d.updateDate
                                        ? FI18n.i18nNext.t(
                                            'cqr_itemmgmt_additem_entry_lastupdated',
                                            {
                                              Timestamp: d.updateDate,
                                            },
                                          )
                                        : FI18n.i18nNext.t(
                                            'cqr_itemmgmt_additem_entry_lastupdated',
                                            {
                                              Timestamp: '—',
                                            },
                                          )
                                    }
                                    type={'additional2'}
                                  />
                                </Space>
                              </div>
                              {!d.isAuth && (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  <FTooltip title={d.authErrorText || '该资源授权链异常，请谨慎签约。'}>
                                    <FIcons.FWarning style={{ fontSize: 14 }} />
                                  </FTooltip>
                                </div>
                              )}
                            </div>
                          </div>
                          {blocked && (
                            <div
                              style={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                              }}
                              onClick={(e) => {
                                return e.stopPropagation();
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <FListFooter
                    state={$listMore}
                    onClickLoadMore={() => {
                      fetchExhibitList(false);
                    }}
                  />
                </div>
              </FSimpleBar>
            )}
          </>
        )}
      </div>
      <AddItemsFromResourcesDrawer
        open={$openResourceDrawer}
        nodeID={nodeID}
        exhibitID={exhibitID}
        onClose={() => {
          set$openResourceDrawer(false);
          void fetchExhibitList(true);
        }}
      />
    </FDrawer>
  );
}

export default AddItemsFromExhibitsDrawer;
